import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, IconButton, Typography,
} from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { fetchExtendedReview } from '../../../../redux/actions';
import { DigitalSection, ExtendedPayload, RootState } from '../../../../redux/types';
import CommentSelector from './CommentSelector';
import MultiReviewComments from './MultiReviewComments';
import { TextblockReviewComment } from '../../../../redux/types/TextblockReviewCommentTypes';
import ApiClient from '../../../../loopback/apiClient';

type Props = {
  trialId: number;
  selectedSection: DigitalSection;
  sections: DigitalSection[];
  enrollmentId: number;
};

type MultiCommentSection = ExtendedPayload['sections'][0];

const TextblockReviewEditor: React.FC<Props> = ({
  trialId,
  selectedSection,
  sections,
  enrollmentId,
}: Props) => {
  const dispatch = useAppDispatch();
  const reviewerId = useAppSelector((s: RootState) => (s.users.token?.userId as number));
  const textblockComments = useAppSelector((s: RootState) => (s.review.extendedReview));
  const [commentState, setCommentState] = useState<ExtendedPayload['sections']>([]);
  const [editMode, setEditMode] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchExtendedReview(trialId));
  }, [
    trialId,
    dispatch,
  ]);

  useEffect(() => {
    ApiClient.getTextblockReviewCommentByEnrollmentId(enrollmentId)
      .then((c) => {
        if (c.length) {
          setCommentState(c[0].payload.sections);
        }
      });
  }, [enrollmentId]);

  const sectionArrIndex = sections.findIndex((s) => s.id === selectedSection.id);

  const onSelectMultiComment = useCallback((commentData: MultiCommentSection): void => {
    const newCommentState: ExtendedPayload['sections'] = [...commentState];
    newCommentState[sectionArrIndex] = commentData;
    setCommentState(newCommentState);
  }, [commentState, sectionArrIndex]);

  const closeEditHandler = () => {
    setEditMode(false);
  };

  const saveCommentsHandler = () => {
    const textblockPayload: Omit<TextblockReviewComment, 'id'> = {
      enrollmentId,
      trialId,
      payload: {
        sections: commentState,
      },
      reviewerId,
    };
    ApiClient.addUpdateTextblockReviewComment(textblockPayload)
      .then((newPayload) => setCommentState(newPayload.payload.sections))
      .finally(closeEditHandler);
  };

  return (
    <Box>
      {
        textblockComments && (
          <>
            <Box display="flex">
              <Typography flexGrow={1} sx={{ my: 2 }} variant="h5">
                <FormattedMessage id="trialSetup.textblockCommentTitle" />
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {
                  editMode && (
                    <>
                      <IconButton color="primary" onClick={saveCommentsHandler}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton color="secondary" onClick={closeEditHandler}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  )
                }
                {
                  !editMode && (
                    <IconButton onClick={() => setEditMode(true)}>
                      <ModeEditIcon />
                    </IconButton>
                  )
                }
              </Box>
            </Box>
            {
              editMode && (
                <CommentSelector
                  data={textblockComments.data[`section${sectionArrIndex + 1}`]}
                  sectionHeading={selectedSection.name}
                  onSelect={onSelectMultiComment}
                  selectedValues={commentState?.[sectionArrIndex] || {}}
                />
              )
            }
            {
              !editMode && sectionArrIndex > -1 && (
                <MultiReviewComments
                  sectionComments={commentState[sectionArrIndex]}
                />
              )
            }
          </>
        )
      }
    </Box>
  );
};

export default TextblockReviewEditor;
